/**
 * Client-side loader for the premium workshop stylesheet (2026-09-09).
 *
 * cowork-ds.css is linked from the server-rendered <head> on the routes that
 * need it (server/premium-css.ts), so a direct hit paints fully styled. A
 * client-side navigation into one of those routes has no such link — this pulls
 * the sheet in on demand. Vite emits the dynamic import as the same standalone
 * asset the server links, so it is one file, fetched and cached once.
 */

import { DS_COWORK_SERIES, isDsCoworkSeries } from "@shared/premium-css-routes";

let requested = false;

/** Idempotent. Call from the render path of a page that carries a ds- scope. */
export function ensurePremiumCss(): void {
  // import.meta.env.SSR is statically true in the SSR build, so the dynamic
  // import below is dropped from entry-server.cjs entirely.
  if (import.meta.env.SSR || typeof document === "undefined") return;
  if (requested) return;
  requested = true;
  // Server-rendered premium route: the stylesheet is already in <head>.
  if (document.querySelector("link[data-premium-css]")) return;
  void import("../styles/cowork-ds.css");
}

/**
 * Workshop-detail variant. `seriesSlug` is authoritative but arrives with the
 * event query; the URL slug is the head start, since a dated session slug is
 * always `<seriesSlug>-<month>-<day>-<year>`. Starting the fetch a beat early
 * is what keeps a client-side navigation from flashing an unstyled page.
 */
export function ensurePremiumCssForSlug(urlSlug: string, seriesSlug?: string | null): void {
  if (
    isDsCoworkSeries(seriesSlug) ||
    (!!urlSlug &&
      DS_COWORK_SERIES.some((s) => urlSlug === s || urlSlug.startsWith(`${s}-`)))
  ) {
    ensurePremiumCss();
  }
}
